import SEO from "@/components/base/SEO";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function SignUp() {
  const navigate = useNavigate();
  const { signup, isLoading } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    if (!name || !email || !password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (!acceptedTerms) {
      setError("Please accept the Terms of Service");
      return;
    }

    const success = await signup(name, email, password);
    if (success) {
      navigate("/home", { replace: true });
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <SEO
        title="Create Account"
        description="Create your PayFlow AI account and take control of your payments with smart reminders and AI insights."
        noindex
      />
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex flex-col justify-center px-5 max-w-[375px] mx-auto w-full">
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "'Pacifico', serif" }}
            >
              PayFlow AI
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Create your account and take control of your payments.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full h-12 bg-white rounded-xl px-4 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all mt-1.5"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full h-12 bg-white rounded-xl px-4 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all mt-1.5"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Password
              </label>
              <div className="relative mt-1.5">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full h-12 bg-white rounded-xl px-4 pr-12 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-lg`}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Confirm Password
              </label>
              <div className="relative mt-1.5">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full h-12 bg-white rounded-xl px-4 pr-12 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className={`${showConfirm ? "ri-eye-off-line" : "ri-eye-line"} text-lg`}></i>
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                  acceptedTerms
                    ? "bg-indigo-600 border-indigo-600"
                    : "border-slate-300 bg-white"
                }`}
              >
                {acceptedTerms && (
                  <i className="ri-check-line text-white text-xs font-bold"></i>
                )}
              </button>
              <p className="text-xs text-slate-500 leading-relaxed">
                I agree to the{" "}
                <span className="text-indigo-600 font-medium">Terms of Service</span>{" "}
                and{" "}
                <span className="text-indigo-600 font-medium">Privacy Policy</span>
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 active:scale-[0.98] transition-transform hover:from-indigo-600 hover:to-purple-700 mt-2 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isLoading ? <><i className="ri-loader-4-line animate-spin"></i>Creating Account...</> : "Create Account"}
            </button>
          </form>
        </div>

        <div className="px-5 pb-8 pt-4 max-w-[375px] mx-auto w-full">
          <p className="text-xs text-slate-400 text-center">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/auth/login")}
              className="text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </>
  );
}